import { useMemo } from 'react';
import { Link as RouterLink, Switch, Route } from 'react-router-dom';
import { Box, Typography } from 'bonfire-kindling';
import { useTranslation, Trans } from 'react-i18next';

import { useVendor } from 'helpers/vendor';
import { useOpportunitiesActiveTab, useOpportunityTypes } from 'helpers/opportunities';
import { regionDataToLocations } from 'helpers/locations';
import { allLocations } from 'constants/regions';
import { PROJECT_STATUS, PROJECT_SOURCE, PROJECT_VIEW_STATUS } from 'constants/opportunities';

// TODO: PV-1447, display an empty message for saved search.
export default function OpportunitiesEmpty({ currentSearchFilter }) {
  const { t } = useTranslation();
  const activeTab = useOpportunitiesActiveTab();

  const vendorQuery = useVendor();
  const vendorLocations = useMemo(
    () => regionDataToLocations(vendorQuery.data?.regionData ?? null),
    [vendorQuery.data?.regionData]
  );

  const { data: availableOpportunityTypes = [] } = useOpportunityTypes();

  const isSearchingOrFiltering = useMemo(() => {
    if (
      currentSearchFilter.search !== '' ||
      currentSearchFilter.statuses !== PROJECT_STATUS.open ||
      currentSearchFilter.viewStatus !== PROJECT_VIEW_STATUS.allStatuses ||
      currentSearchFilter.types.length !== availableOpportunityTypes.length ||
      currentSearchFilter.sources !== PROJECT_SOURCE.allSources
    ) {
      return true;
    }

    if (activeTab === 'recommended') {
      const isFilteringRecommended =
        vendorLocations.length !== currentSearchFilter.locations.length;
      return isFilteringRecommended;
    }

    const isFilteringFollowingOrAll = currentSearchFilter.locations.length !== allLocations.length;
    return isFilteringFollowingOrAll;
  }, [
    currentSearchFilter.locations.length,
    currentSearchFilter.search,
    currentSearchFilter.sources,
    currentSearchFilter.statuses,
    currentSearchFilter.types,
    currentSearchFilter.viewStatus,
    availableOpportunityTypes.length,
    activeTab,
    vendorLocations.length,
  ]);

  return (
    <Box sx={{ margin: 'auto', width: { xs: '80%', sm: '50%' } }}>
      <Switch>
        <Route path="/opportunities/recommended">
          <Box display="flex" alignItems="center" flexDirection="column" textAlign="center">
            {isSearchingOrFiltering ? (
              <Typography variant="h1" component="h3">
                {t('opportunities.empty.no_recommended_search_title')}
              </Typography>
            ) : (
              <>
                <Typography variant="h1" paragraph component="h3">
                  {t('opportunities.empty.no_recommended_title')}
                </Typography>
                <Typography variant="h3" component="h4">
                  {t('opportunities.empty.no_recommended_subtitle')}:
                </Typography>
                <ul style={{ textAlign: 'left', maxWidth: '31rem' }}>
                  <li>
                    <Typography>
                      <Trans i18nKey="opportunities.empty.no_recommended_first_tip">
                        Broaden your
                        <RouterLink to="/settings/commodity-codes">commodity code</RouterLink>,
                        <RouterLink to="/settings/location">location</RouterLink>, or
                        <RouterLink to="/settings/keywords">keyword</RouterLink> criteria.
                      </Trans>
                    </Typography>
                  </li>
                  <li>
                    <Typography>{t('opportunities.empty.no_recommended_second_tip')}</Typography>
                  </li>
                  <li>
                    <Typography>{t('opportunities.empty.no_recommended_third_tip')}</Typography>
                  </li>
                </ul>
              </>
            )}
          </Box>
        </Route>
        <Route path="/opportunities/following">
          <Box textAlign="center">
            <Typography paragraph variant="h1" component="h3">
              {isSearchingOrFiltering
                ? t('opportunities.empty.no_followed_search')
                : t('opportunities.empty.no_followed')}
            </Typography>
            <Typography paragraph variant="body2" component="h4">
              {t('opportunities.empty.expand_followed')}
            </Typography>
          </Box>
        </Route>
        <Route path="/opportunities/all">
          <Box textAlign="center">
            <Typography paragraph variant="h1" component="h3">
              {isSearchingOrFiltering
                ? t('opportunities.empty.no_search_results')
                : t('opportunities.empty.no_results')}
            </Typography>
          </Box>
        </Route>
      </Switch>
    </Box>
  );
}
